import { useState, useEffect, type FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import type { SponsorPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";

export default function PlacementCreatePage() {
  const navigate = useNavigate();
  const [sponsorId, setSponsorId] = useState("");
  const [adSpaceId, setAdSpaceId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [sponsors, setSponsors] = useState<SponsorPublic[]>([]);
  const [adSpaces, setAdSpaces] = useState<
    Array<{
      id: string;
      name: string;
      asset_name: string | null;
      court_name: string;
      school_name: string;
    }>
  >([]);

  useEffect(() => {
    Promise.all([api.getSponsors(), api.getAdSpaces()]).then(
      ([sponsorRes, adSpaceRes]) => {
        setSponsors(sponsorRes.sponsors);
        setAdSpaces(adSpaceRes.ad_spaces);
        if (sponsorRes.sponsors.length > 0) setSponsorId(sponsorRes.sponsors[0].id);
        if (adSpaceRes.ad_spaces.length > 0) setAdSpaceId(adSpaceRes.ad_spaces[0].id);
      },
    );
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const res = await api.createPlacement({
        sponsor_id: sponsorId,
        ad_space_id: adSpaceId,
        start_date: startDate,
        end_date: endDate,
        notes: notes || null,
      });
      navigate(`/placements/${res.placement.id}`);
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : "Failed to create placement");
    } finally {
      setSubmitting(false);
    }
  }

  const selectClass =
    "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

  return (
    <div className="flex flex-col gap-6" data-page="placement-create" data-state={submitting ? "submitting" : error ? "error" : "ready"}>
      <div>
        <h1 className="text-2xl font-semibold">Add Placement</h1>
        <p className="text-muted-foreground">Book an ad space for a sponsor</p>
      </div>

      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle className="text-base">Placement Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-form="placement-create">
            {error && <p className="text-sm text-destructive" data-error>{error}</p>}

            <div className="flex flex-col gap-2">
              <Label htmlFor="sponsor">Sponsor</Label>
              <select
                id="sponsor"
                data-field="sponsor"
                value={sponsorId}
                onChange={(e) => setSponsorId(e.target.value)}
                className={selectClass}
                required
              >
                <option value="" disabled>
                  Select a sponsor
                </option>
                {sponsors.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="adSpace">Ad Space</Label>
              <select
                id="adSpace"
                data-field="ad-space"
                value={adSpaceId}
                onChange={(e) => setAdSpaceId(e.target.value)}
                className={selectClass}
                required
              >
                <option value="" disabled>
                  Select an ad space
                </option>
                {adSpaces.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.school_name} -- {s.court_name} -- {s.asset_name ?? "Asset"} -- {s.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="startDate">Start Date</Label>
              <Input
                id="startDate"
                data-field="start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="endDate">End Date</Label>
              <Input
                id="endDate"
                data-field="end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                required
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="notes">Notes</Label>
              <Input
                id="notes"
                data-field="notes"
                placeholder="Optional notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button type="submit" data-action="submit" disabled={submitting || !sponsorId || !adSpaceId}>
                {submitting ? "Creating..." : "Create Placement"}
              </Button>
              <Link to="/placements" className={buttonVariants({ variant: "outline" })} data-action="cancel">
                Cancel
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
